import { useEffect, useState } from "react";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { getUserAttributes } from "../../utils/auth";
import IntegrationService from "../../services/integrations/Integrations";
import { getColumns, initialSort } from "./IntegrationTableConfig";
import BasicDatatable from "../Common/BasicDatatable";
import { useQueryClient } from "@tanstack/react-query";
import useGlobalContext from "../../context/useGlobalContext";
import { Box } from "@mui/system";
import UserInteractionsService from "../../services/interactions/Interactions";
import CommonService from "../../services/common/commonService";

interface UserLevelSettingsProps {
  userSettingsData?: any;
  isUserSettingsLoading: boolean;
  isUserSettingsFetching: boolean;
  noteTakerFilter: string;
  setNoteTakerFilter: React.Dispatch<React.SetStateAction<string>>;
  calendarFilter: string;
  setCalendarFilter: React.Dispatch<React.SetStateAction<string>>;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
  sort: {sort_order: "desc" | "asc";};
  setSort: React.Dispatch<React.SetStateAction<{sort_order: "asc" | "desc";}>>;
  searchQuery?: string;
}
interface UserLevelSettingsType {
  profileOrgId: string;
  fullName: string;
  email: string;
  department: string;
  analyzer: string;
  calendar: string;
  lastSyncTime?: string;
}

const generateRows = (integrations: any[], activeUsers: any, handleManagerUpdate?: (employeeProfileOrgId: string, managerProfileOrgId: string | null) => void): UserLevelSettingsType[] =>
    integrations.map((item: any) => ({
    profileOrgId: item.profileOrgId,
    fullName: item.fullName,
    email: item.employeeEmail,
    department: item.empDepartment,
    emailSyncEnabled: item.isEmailSyncEnabled !== undefined? item.isEmailSyncEnabled ? "Enabled" : "Disabled"
        : "-",
    analyzer: item.isBotSchedulingDisabled !== undefined
        ? item.isBotSchedulingDisabled ? "Disabled" : "Enabled"
        : "-",
    calendar: item.isSyncEnabled !== undefined
        ? item.isSyncEnabled ? "Enabled" : "Disabled"
        : "-",
    activeUsers: activeUsers,
    employeeProfileId: item.employeeProfileId,
    managerProfileOrgId: item.managerProfileOrgId,
    managerProfileId: item.managerProfileId,
    managerName: item.managerName,
    handleManagerUpdate,
    userBotSettings: item.userBotSettings,
    }));


const UserLevelSettings: React.FC<UserLevelSettingsProps> = ({
    userSettingsData,
    isUserSettingsLoading,
    isUserSettingsFetching,
    noteTakerFilter,
    setNoteTakerFilter,
    calendarFilter,
    setCalendarFilter,
    onPageChange,
    onPageSizeChange,
    setSort,
    searchQuery,
}) => {
    const { showSnackbar } = useDisplaySnackbar();
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const loggedInEmail = getUserAttributes()?.email;
    const [rows, setRows] = useState<any[]>([]);
    const [ totalRecords, setTotalRecords ] = useState(0);
    const queryClient = useQueryClient();
    const { featureMap, accountDetails, setUserTeamData } = useGlobalContext();
    const isBotSettingsEnabled = featureMap?.user_level_bot_setting === true;
    const loggedInProfileOrgId = accountDetails?.profileOrgId ?? "";
    const [activeUsers, setActiveUsers] = useState<any[]>([]);

    const {
        isSuccess,
        isLoading: isActiveUsersLoading,
        isFetching: isActiveUsersFetching,
        isError,
        error,
        data: activeUsersData,
    } = UserInteractionsService.useGetActiveUsers( {}, true );

    useEffect(() => {
        if(isSuccess) {
          const activeUsers = activeUsersData?.data?.data || [];
          setActiveUsers(activeUsers);
        }
        if (isError) {
          showSnackbar("error", `Error fetching active users: ${error}`);
        }
    }, [isError, error, activeUsersData, isSuccess, isActiveUsersLoading, isActiveUsersFetching]);

    const { mutate: updateUserSettings } = IntegrationService.useUpdateUserSettings({
        onSuccess: () => {
            showSnackbar("success", "Updated user settings");
            queryClient.invalidateQueries({
                queryKey: ["get_user_level_settings"],
            });
            queryClient.invalidateQueries({
                queryKey: ["get_calendar_details"],
            });
        },
        onError: () => {
            showSnackbar("error", "Failed to update user settings");
        },
    });

    const {
        isSuccess: isHierarchySuccess,
        isError: isHierarchyError,
        error: userHierachyError,
        data: userHierarchyData,
        refetch,
    } = CommonService.useGetHierarchyInfo({
        employeePorId : loggedInProfileOrgId || "",
        depthLevel : 1
    }, !!loggedInProfileOrgId);

    useEffect(() => {
        if (isHierarchySuccess) {
        const result = userHierarchyData?.data;

        if (typeof result === "string") {
            setUserTeamData({
            userTeamDataMessage: "",
            showTeamTab: false,
            });// Show the team tab only if the message isn't a no-team message
        } else if (Array.isArray(result) && result.length === 0) {
            // Empty array = no team
            setUserTeamData({
            userTeamDataMessage: "",
            showTeamTab: false,
            });
        } else {
            setUserTeamData({
            userTeamDataMessage: "",
            showTeamTab: true,
            }); // Show the team tab when there's valid team data
        }
        } else if (isHierarchyError) {
        showSnackbar("error", userHierachyError);
        setUserTeamData({
            userTeamDataMessage: "",
            showTeamTab: false,
        });
        }
    }, [isHierarchySuccess, isHierarchyError, userHierarchyData]);

    const { mutate: updateUserHeirarchyMutate } = IntegrationService.useUpdateUserHeirarchy({
        onSuccess: () => {
        showSnackbar("success", "Manager hierarchy updated successfully");

        queryClient.invalidateQueries({
            queryKey: ["get_user_level_settings"],
        });
        refetch()
        },
        onError: (error: any) => {
        showSnackbar("error", error?.response?.data?.message ||"Failed to update manager");
        },
    });

    const handleManagerUpdate = (
        employeeProfileId: string,
        managerProfileId: string | null
    ) => {
        updateUserHeirarchyMutate({
            employeeProfileId,
            managerProfileId: managerProfileId || undefined,
        });
    };
    const handleBotScheduleChange = (profileOrgId: string, value: string) => {
        updateUserSettings({
            userSettings: [
                {
                    profileOrgRelationshipId: profileOrgId,
                    isBotSchedulingDisabled: value === "disabled",
                }
            ]
        });
    };

    const handleSyncChange = (profileOrgId: string, value: string) => {
        updateUserSettings({
            userSettings: [
                {
                    profileOrgRelationshipId: profileOrgId,
                    isSyncEnabled: value === "enabled",
                }
            ]
        });
    };

    const handleEmailChange = (profileOrgId: string, value: string) => {
        updateUserSettings({
            userSettings: [
                {
                    profileOrgRelationshipId: profileOrgId,
                    isEmailSyncEnabled: value === "enabled",
                }
            ]
        });
    }
    const UserSettingsData = userSettingsData;

    useEffect(() => {
        setShowSpinner(isUserSettingsLoading || isUserSettingsFetching)
        if (UserSettingsData?.data?.length > 0) {
            setRows(generateRows(UserSettingsData.data, activeUsers, handleManagerUpdate));
            setTotalRecords(UserSettingsData.totalRecords);
        } else {
            setRows([]);
            setTotalRecords(0);
        }
    }, [
        isUserSettingsLoading,
        isUserSettingsFetching,
        UserSettingsData,
        loggedInEmail,
        activeUsers
    ]);

    const { mutateAsync: upsertBotSettings } = IntegrationService.useUpsertBotSettings({
        onSuccess: () => {
        showSnackbar("success", "Updated Note Taker Settings Successfully.")
        queryClient.invalidateQueries({
            queryKey: ["get_user_level_settings"],
        });
        },
    });

    const handleScheduleChange = async (profileOrgId: string, value: "all" | "external" | "internal" | "ondemand") => {
        await upsertBotSettings({
            botSettingType: value,
            profileOrgId,
        });
    };

    const columns = getColumns(
        handleBotScheduleChange,
        handleSyncChange,
        noteTakerFilter,
        setNoteTakerFilter,
        calendarFilter,
        setCalendarFilter,
        handleEmailChange,
        searchQuery,
        handleScheduleChange,
    );

    const onSortingChange = (sorting: any[]) => {
        if (sorting && sorting.length > 0) {
        setSort({
            sort_order: sorting[0].desc === true ? "desc" : "asc",
        });
        }
    };

    const hideFooter = (totalRecords <= 1);
    const hasActiveFilters = Boolean(noteTakerFilter) || Boolean(calendarFilter);

    return (
        < Box
        sx={{
            height: hasActiveFilters ? "calc(100vh - 220px)": "calc(100vh - 160px)",
            overflowY: "auto",
            overflowX: "hidden",
        }}>
            <BasicDatatable
            columns={columns}
            rows={rows}
            rowCount={totalRecords}
            isBordervisible={true}
            onPageSizeChange={onPageSizeChange}
            onPaginationChange={onPageChange}
            showSpinner={showSpinner}
            hidePaginationFooter={hideFooter}
            minHeight="calc(10vh)"
            height="auto"
            maxHeight={hasActiveFilters ? "calc(60vh)" : "calc(70vh)"}
            onSortingChange={onSortingChange}
            initialSort={initialSort}
            columnVisibility={{ userBotSettings: isBotSettingsEnabled ? true : false }}
            />
        </Box>
    );
};

export default UserLevelSettings;
